import type { PermissionCode } from '@ldembalagens/types';
import {
  BarChart3,
  Bell,
  ClipboardList,
  Factory,
  FileText,
  Folder,
  Forklift,
  Globe,
  HandCoins,
  IdCard,
  LayoutDashboard,
  Package,
  Recycle,
  Settings,
  ShoppingCart,
  Store,
  Timer,
  Truck,
  Users,
  Warehouse,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  label: string;
  /** Rótulo no trilho lateral quando `label` não cabe. */
  shortLabel?: string;
  to: string;
  icon: LucideIcon;
  /** Qualquer uma destas permissões libera o item. */
  permissions?: PermissionCode[];
  /** Fase do roadmap em que o módulo é entregue. */
  phase: number;
  children?: { label: string; to: string; permissions?: PermissionCode[] }[];
  /** `footer`: fixado na base da sidebar (fora da lista rolável). */
  placement?: 'footer';
}

/** Fase entregue atualmente. Itens de fases posteriores exibem página "em desenvolvimento". */
export const DELIVERED_PHASE = 9;

export const NAVIGATION: NavItem[] = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: LayoutDashboard,
    permissions: ['dashboard:read'],
    phase: 1,
  },
  { label: 'Produtos', to: '/produtos', icon: Package, permissions: ['products:read'], phase: 2 },
  {
    label: 'Estoque',
    to: '/estoque',
    icon: Warehouse,
    permissions: ['stock:read', 'materials:read'],
    phase: 2,
    children: [
      { label: 'Posição', to: '/estoque', permissions: ['stock:read'] },
      { label: 'Materiais', to: '/estoque/materiais', permissions: ['materials:read'] },
      { label: 'Movimentações', to: '/estoque/movimentacoes', permissions: ['stock:read'] },
      { label: 'Inventário', to: '/estoque/inventario', permissions: ['stock:read'] },
    ],
  },
  {
    label: 'Produção',
    to: '/producao',
    icon: Factory,
    permissions: ['production:read'],
    phase: 3,
    children: [
      { label: 'Visão geral', to: '/producao' },
      { label: 'Ordens de produção', to: '/producao/ordens' },
      { label: 'Apontamentos', to: '/producao/apontamentos' },
      { label: 'Qualidade', to: '/producao/qualidade', permissions: ['quality:read'] },
    ],
  },
  { label: 'Clientes', to: '/clientes', icon: Users, permissions: ['customers:read'], phase: 2 },
  {
    label: 'Fornecedores',
    to: '/fornecedores',
    icon: Store,
    permissions: ['suppliers:read'],
    phase: 2,
  },
  {
    label: 'Vendas',
    to: '/vendas',
    icon: ShoppingCart,
    permissions: ['sales:read'],
    phase: 3,
    children: [
      { label: 'Orçamentos', to: '/vendas/orcamentos' },
      { label: 'Pedidos', to: '/vendas/pedidos' },
    ],
  },
  {
    label: 'Compras',
    to: '/compras',
    icon: ClipboardList,
    permissions: ['purchases:read'],
    phase: 3,
    children: [{ label: 'Pedidos de compra', to: '/compras/pedidos' }],
  },
  {
    label: 'Financeiro',
    to: '/financeiro',
    icon: HandCoins,
    permissions: ['financial:read'],
    phase: 4,
    children: [
      { label: 'Visão geral', to: '/financeiro' },
      { label: 'Entradas', to: '/financeiro/entradas' },
      { label: 'Saídas', to: '/financeiro/saidas' },
      { label: 'Contas a pagar', to: '/financeiro/contas-pagar' },
      { label: 'Contas a receber', to: '/financeiro/contas-receber' },
      { label: 'Impostos', to: '/financeiro/impostos' },
      { label: 'Empréstimos', to: '/financeiro/emprestimos' },
      { label: 'Fluxo de caixa', to: '/financeiro/fluxo-caixa' },
      { label: 'Contas', to: '/financeiro/contas' },
    ],
  },
  {
    label: 'RH',
    to: '/rh',
    icon: IdCard,
    permissions: ['hr:read'],
    phase: 5,
    children: [
      { label: 'Visão geral', to: '/rh' },
      { label: 'Colaboradores', to: '/rh/colaboradores' },
      { label: 'Lançamentos', to: '/rh/lancamentos' },
      { label: 'Adiantamentos', to: '/rh/adiantamentos' },
      { label: 'Vales', to: '/rh/vales' },
      { label: 'Férias e afastamentos', to: '/rh/ferias' },
    ],
  },
  {
    label: 'Patrimônio',
    to: '/patrimonio',
    icon: Forklift,
    permissions: ['assets:read'],
    phase: 5,
    children: [
      { label: 'Visão geral', to: '/patrimonio' },
      { label: 'Equipamentos', to: '/patrimonio/equipamentos' },
      { label: 'Veículos', to: '/patrimonio/veiculos' },
    ],
  },
  {
    label: 'Manutenções',
    to: '/manutencoes',
    icon: Wrench,
    permissions: ['maintenance:read'],
    phase: 5,
    children: [
      { label: 'Ordens', to: '/manutencoes/ordens' },
      { label: 'Preventivas', to: '/manutencoes/preventivas' },
    ],
  },
  {
    label: 'Horas de máquina',
    shortLabel: 'Horas máq.',
    to: '/horas-maquina',
    icon: Timer,
    permissions: ['machines:read'],
    phase: 9,
    children: [
      { label: 'Apontamentos', to: '/horas-maquina' },
      { label: 'Faturas', to: '/horas-maquina/faturas' },
    ],
  },
  {
    label: 'MTR',
    to: '/mtr',
    icon: Recycle,
    permissions: ['waste:read'],
    phase: 9,
  },
  {
    label: 'Notas fiscais',
    to: '/notas-fiscais',
    icon: FileText,
    permissions: ['invoices:read'],
    phase: 6,
    children: [
      { label: 'Notas', to: '/notas-fiscais' },
      { label: 'SEFAZ', to: '/notas-fiscais/sefaz' },
      { label: 'Nova nota', to: '/notas-fiscais/nova', permissions: ['invoices:create'] },
    ],
  },
  {
    label: 'Documentos',
    to: '/documentos',
    icon: Folder,
    permissions: ['documents:read'],
    phase: 6,
  },
  {
    label: 'Logística',
    to: '/logistica',
    icon: Truck,
    permissions: ['logistics:read'],
    phase: 6,
    children: [
      { label: 'Separação', to: '/logistica' },
      { label: 'Entregas', to: '/logistica/entregas' },
    ],
  },
  {
    label: 'Alertas',
    to: '/alertas',
    icon: Bell,
    permissions: ['dashboard:read'],
    phase: 6,
  },
  {
    label: 'Site',
    to: '/site',
    icon: Globe,
    permissions: ['settings:read', 'products:read', 'sales:read'],
    phase: 8,
    children: [
      { label: 'Visão geral', to: '/site' },
      { label: 'Conteúdo', to: '/site/conteudo', permissions: ['settings:read'] },
      { label: 'Catálogo', to: '/site/catalogo', permissions: ['products:read'] },
      { label: 'Solicitações', to: '/site/solicitacoes', permissions: ['sales:read'] },
    ],
  },
  {
    label: 'Relatórios',
    to: '/relatorios',
    icon: BarChart3,
    permissions: ['reports:read'],
    phase: 7,
  },
  {
    label: 'Configurações',
    to: '/configuracoes',
    icon: Settings,
    permissions: ['settings:read', 'users:read', 'roles:read', 'audit:read'],
    phase: 1,
    placement: 'footer',
    children: [
      { label: 'Empresa', to: '/configuracoes/empresa', permissions: ['settings:read'] },
      {
        label: 'Cadastros auxiliares',
        to: '/configuracoes/cadastros',
        permissions: ['products:read', 'materials:read', 'stock:read'],
      },
      {
        label: 'Financeiro',
        to: '/configuracoes/financeiro',
        permissions: ['financial:read'],
      },
      { label: 'Usuários', to: '/configuracoes/usuarios', permissions: ['users:read'] },
      {
        label: 'Perfis e permissões',
        to: '/configuracoes/permissoes',
        permissions: ['roles:read'],
      },
      { label: 'Auditoria', to: '/configuracoes/auditoria', permissions: ['audit:read'] },
    ],
  },
];

/** Atalhos fixos da navegação inferior (mobile); "Mais" abre os demais módulos. */
export const MOBILE_PRIMARY_ROUTES = ['/dashboard', '/estoque', '/vendas'] as const;
export const MOBILE_LABELS: Record<string, string> = {
  '/dashboard': 'Início',
  '/estoque': 'Estoque',
  '/vendas': 'Vendas',
};

/** Mapa de rota → rótulo, usado pelos breadcrumbs. */
const routeLabelEntries: [string, string][] = [
  ...NAVIGATION.flatMap((item): [string, string][] => [
    [item.to, item.label],
    ...(item.children ?? []).map((c): [string, string] => [c.to, c.label]),
  ]),
  ['/perfil', 'Meu perfil'],
  ['/ajuda', 'Ajuda'],
  ['/clientes/novo', 'Novo cliente'],
  ['/fornecedores/novo', 'Novo fornecedor'],
  ['/produtos/novo', 'Novo produto'],
  ['/estoque/materiais/novo', 'Novo material'],
  ['/vendas/orcamentos/novo', 'Novo orçamento'],
  ['/vendas/pedidos/novo', 'Novo pedido'],
  ['/compras/pedidos/novo', 'Nova solicitação'],
  ['/producao/ordens/nova', 'Nova ordem'],
  ['/rh/colaboradores/novo', 'Novo colaborador'],
  ['/patrimonio/novo', 'Novo ativo'],
  ['/manutencoes/ordens', 'Ordens'],
  ['/notas-fiscais/nova', 'Nova nota'],
  ['/notas-fiscais/sefaz', 'SEFAZ'],
  ['/mtr/novo', 'Novo MTR'],
  ['/logistica/entregas', 'Entregas'],
  ['/relatorios', 'Relatórios'],
  ['/admin', 'Administração'],
  ['/admin/empresas', 'Empresas'],
];

export const ROUTE_LABELS: Record<string, string> = Object.fromEntries(routeLabelEntries);

/** `true` quando `pathname` é a rota `to` ou está abaixo dela. */
export function isUnderRoute(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** Módulo (item de 1º nível) ao qual a rota atual pertence; prefere o prefixo mais longo. */
export function findActiveModule(pathname: string): NavItem | undefined {
  return NAVIGATION.filter((item) => isUnderRoute(pathname, item.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
}

/** Subitem ativo de um módulo: o de prefixo mais longo que contém a rota atual. */
export function findActiveChild<T extends { to: string }>(
  pathname: string,
  children: readonly T[],
): T | undefined {
  return children
    .filter((c) => isUnderRoute(pathname, c.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
}
